import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../../shared-components/button/Button';
import CheckBoxButtons from '../../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomFileInput from '../../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import deviceApi from '../../../../utils/api/device-hub';

interface AddFirmwareVersionDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Called after a firmware version is successfully created */
  onFirmwareVersionCreated: () => void;
}

interface OptionType {
  value: string;
  label: string;
}

/**
 * Slide-in drawer modal for uploading a new firmware version artifact.
 * @param {object} properties - The properties object.
 * @param {boolean} properties.open - Whether the modal is open.
 * @param {Function} properties.onClose - Callback to close the modal.
 * @param {Function} properties.onFirmwareVersionCreated - Callback called after a firmware version is successfully created.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AddFirmwareVersionDrawer({
  open,
  onClose,
  onFirmwareVersionCreated,
}: Readonly<AddFirmwareVersionDrawerProperties>) {
  const { t } = useTranslation('station');

  const [brandSelect, setBrandSelect] = useState('');
  const [modelSelect, setModelSelect] = useState('');
  const [version, setVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [firmwareFile, setFirmwareFile] = useState<File[]>([]);
  const [compatibleModels, setCompatibleModels] = useState<string[]>([]);

  const [brandError, setBrandError] = useState('');
  const [modelError, setModelError] = useState('');
  const [versionError, setVersionError] = useState('');
  const [fileError, setFileError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [brandOptions, setBrandOptions] = useState<OptionType[]>([]);
  const [modelOptions, setModelOptions] = useState<OptionType[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  /**
   * Fetches active brand list from the API and updates brand options state.
   * @returns {Promise<void>} Resolves once brand options are set.
   */
  const loadBrands = async () => {
    try {
      const response = await deviceApi.fetchBrands({ paginate: false, status: ['approved', 'pending'] });
      const data = response.data?.results || response.data || [];
      const brands = data.map((brand: any) => ({ value: brand._id, label: brand.name }));
      setBrandOptions(brands);
    } catch (error: any) {
      console.error('Failed to fetch brands:', error);
    }
  };

  /**
   * Fetches active models for the given brand, following the same
   * brand-scoped fetch pattern used by AddDevice.tsx's loadModels.
   * @param {string} brandId - The selected brand id
   */
  const loadModels = (brandId: string) => {
    const selectedBrand = brandOptions.find((brand) => brand.value === brandId);
    if (!selectedBrand) {
      setModelOptions([]);
      return;
    }

    setModelOptions([]);
    setModelsLoading(true);

    deviceApi
      .fetchModels({ brandName: selectedBrand.label, paginate: false, status: ['approved', 'pending'] })
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const models = data.map((model: any) => ({ value: model._id, label: model.name }));
        setModelOptions(models);
      })
      .catch((error) => {
        console.error('Failed to fetch models:', error);
        setModelOptions([]);
      })
      .finally(() => setModelsLoading(false));
  };

  useEffect(() => {
    if (open) {
      loadBrands();
    }
  }, [open]);

  useEffect(() => {
    if (!brandSelect) {
      setModelOptions([]);
      return;
    }
    loadModels(brandSelect);
  }, [brandSelect, brandOptions]);

  // Compatible-models checkbox list: scoped to the selected brand's other
  // models only, excluding the primary model already selected above.
  const compatibleModelOptions = useMemo(
    () => modelOptions.filter((option) => option.value !== modelSelect),
    [modelOptions, modelSelect],
  );

  /**
   * Resets all form fields to their initial state.
   */
  const resetForm = () => {
    setBrandSelect('');
    setModelSelect('');
    setVersion('');
    setReleaseNotes('');
    setFirmwareFile([]);
    setCompatibleModels([]);
    setBrandError('');
    setModelError('');
    setVersionError('');
    setFileError('');
    setShowFormErrors(false);
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validates and submits the firmware version upload form.
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    setBrandError('');
    setModelError('');
    setVersionError('');
    setFileError('');

    if (!brandSelect || !modelSelect || !version.trim() || firmwareFile.length === 0) {
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append('brandId', brandSelect);
    formData.append('modelId', modelSelect);
    formData.append('version', version.trim());
    formData.append('releaseNotes', releaseNotes.trim());
    compatibleModels.forEach((compatibleModelId, index) => {
      formData.append(`compatibleModels[${index}]`, compatibleModelId);
    });
    formData.append('file', firmwareFile[0]);

    deviceApi
      .uploadFirmwareArtifact(formData, true)
      .then(() => {
        setShowSuccess(true);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to upload firmware version';
        const lowerMessage = message.toLowerCase();
        if (lowerMessage.includes('version')) {
          setVersionError(message);
        } else if (lowerMessage.includes('model')) {
          setModelError(message);
        } else if (lowerMessage.includes('brand')) {
          setBrandError(message);
        } else {
          setFileError(message);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content">
            <div className="drawer-header">
              <h3 className="drawer-title">{t('addFirmwareVersion')}</h3>
              <p className="drawer-subtitle">{t('uploadFirmwareDescription')}</p>
            </div>
            <div className="drawer-body">
              <SelectField
                name="firmwareBrandSelect"
                inputLabel={t('brand')}
                options={brandOptions}
                placeholder={t('chooseBrand')}
                selectedValue={brandSelect}
                onChangeValue={(_, value) => {
                  setBrandSelect(value.toString());
                  setModelSelect('');
                  setCompatibleModels([]);
                  if (brandError) setBrandError('');
                }}
                error={Boolean((showFormErrors && !brandSelect) || brandError)}
                errorMessage={brandError || t('brandRequired')}
                valueRequired
                enableSearch
              />
              <SelectField
                name="firmwareModelSelect"
                inputLabel={t('model')}
                options={modelOptions}
                placeholder={t('chooseModel')}
                selectedValue={modelSelect}
                onChangeValue={(_, value) => {
                  setModelSelect(value.toString());
                  if (modelError) setModelError('');
                }}
                error={Boolean((showFormErrors && !modelSelect) || modelError)}
                errorMessage={modelError || t('modelRequired')}
                valueRequired
                enableSearch
                disabled={!brandSelect}
                loading={modelsLoading}
              />
              <CustomInput
                type="text"
                name="version"
                inputLabel={t('version')}
                placeholder={t('enterVersion')}
                value={version}
                onChangeValue={(_, value) => {
                  setVersion(value.toString());
                  if (versionError) setVersionError('');
                }}
                error={Boolean((showFormErrors && !version.trim()) || versionError)}
                errorMessage={versionError || t('versionRequired')}
                valueRequired
                characterLimit={50}
              />
              {/*
                No shared textarea component exists in this codebase (CustomInput
                only renders a native <input>). Reusing CustomInput's own
                custom-input-wrap/input-label/custom-input-container class
                structure here (verified in CustomInput.tsx) rather than
                inventing a new shared component, so the field still inherits
                the same visual language.
              */}
              <div className="custom-input-wrap large">
                <p className="input-label">{t('releaseNotes')}</p>
                <div className="custom-input-container textarea-container">
                  <textarea
                    className="custom-textarea"
                    name="releaseNotes"
                    placeholder={t('enterReleaseNotes')}
                    value={releaseNotes}
                    maxLength={1000}
                    rows={4}
                    onChange={(event) => setReleaseNotes(event.target.value)}
                  />
                </div>
              </div>
              <CustomFileInput
                inputLabel={t('uploadFirmwareFile')}
                acceptedFileType=".bin,.hex,.zip"
                placeholder={t('uploadFirmwareFilePlaceholder')}
                name="firmwareFile"
                selectedFiles={firmwareFile}
                onChangeValue={(_, files) => {
                  setFirmwareFile(files);
                  if (fileError) setFileError('');
                }}
                error={Boolean((showFormErrors && firmwareFile.length === 0) || fileError)}
                errorMessage={fileError || t('uploadFirmwareFileRequired')}
                valueRequired
                maxFileCount={1}
              />
              <CheckBoxButtons
                inputLabel={t('compatibleModels')}
                name="compatibleModels"
                list={compatibleModelOptions}
                value={compatibleModels}
                onChangeValue={(_, value) => setCompatibleModels(value as string[])}
              />
            </div>
            <div className="drawer-footer">
              <MyButton label={t('cancel')} buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label={t('uploadFirmware')}
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('firmwareVersionAddedSuccessfully')}
            description={t('firmwareVersionAddedSuccessfullyDescription')}
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onFirmwareVersionCreated();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onFirmwareVersionCreated();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default AddFirmwareVersionDrawer;
